/**
 * Truncated drop cell for stats table.
 * - Click drop ID: copies to clipboard
 * - Click leads list: copies to clipboard
 * - Click GW SID: navigates to drop page
 * - Static tag: from our app when present, else default "SMS" for external drops.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import { toSentAtFormat } from '@/features/smswiser-campaigns/utils/dateFormat';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';

import { StatsHoverPopover } from '../../../StatsHoverPopover';
import type { DropEmojiTag } from '../../../shared/utils/drop-tag-display';
import { DropInfoTooltipContent } from '../DropInfoTooltipContent';
import { DropCellTagChips } from './DropCellTagChips';
import { orDash, truncateEnd } from '../../../shared';

function formatSequenceTooltip(drop: DropsWithStatsRow['drop'], dropSequence?: number): string {
  const parts: string[] = [];
  const executeStr = toSentAtFormat(drop.executeAfter);
  const finishedStr = toSentAtFormat(drop.finishedAt);
  const createdStr = toSentAtFormat(drop.createdAt);
  if (executeStr) parts.push(`Execute after: ${executeStr}`);
  else if (finishedStr) parts.push(`Sent at: ${finishedStr}`);
  else if (createdStr) parts.push(`Created at: ${createdStr}`);
  if (dropSequence != null) parts.push(`Send order #${dropSequence}`);
  return parts.join(' · ') || 'Send order (oldest first)';
}

export const TruncatedDropCell: FC<{
  drop: DropsWithStatsRow['drop'];
  dropSequence?: number;
  dropPageHref: string;
  returnTo?: string;
  /** Country slug — forwarded to the popup so it can deep-link to the Tool view. */
  countryKey?: string;
  /** Performance tags from drop-performance-tags API */
  performanceTags?: string[];
  /** Source-rule emojis — bare, slightly larger, no chip background. */
  emojiTags?: DropEmojiTag[];
  /** Custom colors for performance tags (rule name -> hex) */
  tagColors?: Record<string, string>;
}> = ({ drop, dropSequence, dropPageHref, returnTo, countryKey, performanceTags, emojiTags, tagColors }) => {
  const leads = truncateEnd(orDash(drop.leadsList), 32);
  const sequenceTooltip = formatSequenceTooltip(drop, dropSequence);
  const { success: toastSuccess, error: toastError } = useToast();

  const gw = drop.smsGatewayRoute?.trim() || null;
  const hasGw = Boolean(gw);

  const dropInfoContent = (
    <DropInfoTooltipContent
      drop={drop}
      dropSequence={dropSequence}
      countryKey={countryKey}
    />
  );

  const handleCopyId = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!drop.uniqueId) return;
    navigator.clipboard
      .writeText(drop.uniqueId)
      .then(() => toastSuccess('Drop ID copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const handleCopyLeadsList = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const text = leads.full || orDash(drop.leadsList);
    if (!text || text === '—') return;
    navigator.clipboard
      .writeText(text)
      .then(() => toastSuccess('Leads list copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      {dropSequence != null && (
        <span
          className="shrink-0 font-mono tabular-nums text-muted-foreground/70"
          title={sequenceTooltip}
        >
          {dropSequence}
        </span>
      )}
      {drop.uniqueId ? (
        <button
          type="button"
          onClick={handleCopyId}
          title="Click to copy drop ID"
          className="shrink-0 font-mono font-medium text-foreground cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary"
          aria-label="Copy drop ID"
        >
          {drop.uniqueId}
        </button>
      ) : null}
      <Link
        to={dropPageHref}
        state={returnTo ? { returnTo } : undefined}
        className="min-w-0 flex-1 flex items-center justify-between gap-1 rounded -m-1 p-1 cursor-pointer"
        title="Open drop page"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleCopyLeadsList}
          title="Click to copy leads list"
          className="min-w-0 truncate text-left text-inherit bg-transparent border-0 p-0 cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary"
          aria-label="Copy leads list"
        >
          {leads.display}
        </button>
        <span className="inline-flex shrink-0 items-center gap-1">
          <DropCellTagChips
            staticTag={drop.staticTag}
            performanceTags={performanceTags}
            emojiTags={emojiTags}
            tagColors={tagColors}
          />
          <StatsHoverPopover content={dropInfoContent}>
            <span
              className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 font-medium"
              tabIndex={0}
            >
              <span className={cn(hasGw ? 'text-foreground/80' : 'text-warning')}>GW</span>
              <span className="text-foreground/80">SID</span>
            </span>
          </StatsHoverPopover>
        </span>
      </Link>
    </div>
  );
};
